import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { DEFAULT_DATE_PRESETS } from './constants';
import { CustomRangeInputs } from './CustomRangeInputs';
import { PresetList } from './PresetList';
import type { DatePreset, DateRange } from './types';

export { DEFAULT_DATE_PRESETS };
export type { DatePreset, DateRange };

interface Props {
  startTime: string;
  endTime: string;
  onChange: (range: DateRange) => void;
  presets?: DatePreset[];
}

export const DateRangePicker: FC<Props> = ({
  startTime,
  endTime,
  onChange,
  presets = DEFAULT_DATE_PRESETS,
}) => {
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null);

  useEffect(() => {
    if (!startTime && !endTime) {
      setSelectedPreset(null);
      return;
    }
    const matched = presets.find((preset) => {
      const range = preset.getRange();
      return range?.startTime === startTime && range?.endTime === endTime;
    });
    setSelectedPreset((current) => {
      if (matched) return matched.id;
      if (current === 'custom') return 'custom';
      return 'custom';
    });
  }, [startTime, endTime, presets]);

  const handleStartTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedPreset(null); // Clear preset when manually editing
    onChange({
      startTime: e.target.value,
      endTime,
    });
  };

  const handleEndTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedPreset(null); // Clear preset when manually editing
    onChange({
      startTime,
      endTime: e.target.value,
    });
  };

  const applyPreset = (preset: DatePreset) => {
    const range = preset.getRange();
    if (preset.id !== 'custom') {
      onChange(range);
    }
    setSelectedPreset(preset.id);
  };

  return (
    <div>
      <PresetList
        presets={presets}
        selectedPreset={selectedPreset}
        onPresetClick={applyPreset}
      />
      {selectedPreset === 'custom' && (
        <CustomRangeInputs
          startTime={startTime}
          endTime={endTime}
          onStartTimeChange={handleStartTimeChange}
          onEndTimeChange={handleEndTimeChange}
        />
      )}
    </div>
  );
};

export default DateRangePicker;
